import { Position } from "@/icons/Position";
import { TypographyKeys } from "@/theme/typography";
import React, { ElementType } from "react";

type BasePositionLinkProps = {
  isExternal?: boolean;
  fs?: TypographyKeys;
  children: React.ReactNode;
  className?: string;
  iconPosition?: "left" | "right";
};

type PositionLinkAsLinkProps = BasePositionLinkProps & {
  href: string;
  asLink?: "a" | ElementType;
  onClick?: never;
  type?: never;
  asButton?: never;
  iconVisible?: boolean;
};

type PositionLinkAsButtonProps = BasePositionLinkProps & {
  onClick?: () => void;
  type?: "button";
  asButton?: true;
  asLink?: never;
  href?: never;
  iconVisible?: boolean;
};

type PositionLinkProps = PositionLinkAsLinkProps | PositionLinkAsButtonProps;

export const PositionLink = ({
  href,
  asLink = "a",
  isExternal = false,
  fs = "6",
  children,
  className = "",
  onClick,
  type = "button",
  asButton,
  iconVisible = true,
  iconPosition = "left",
  ...props
}: PositionLinkProps) => {
  const createFontSizeClass = (size: TypographyKeys) => {
    return `fs-${size}`;
  };

  const fontSizeClass = createFontSizeClass(fs);
  const baseClasses = `max-w-fit text-decoration-none text-primary base-row align-items-center fw-bold gap-x8 hover-underline cursor-pointer ${fontSizeClass} ${className}`;

  const Icon = Position;

  if (asButton) {
    return (
      <button
        className={`bg-transparent border-none ${baseClasses}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onClick) {
            onClick();
          }
        }}
        type={type}
        {...props}
      >
        {iconVisible && iconPosition === "left" && (
          <Icon color={"primary"} width={24} height={24} />
        )}
        {children}
        {iconVisible && iconPosition === "right" && (
          <Icon color={"primary"} width={24} height={24} />
        )}
      </button>
    );
  }

  const LinkComponent = asLink;

  return (
    <LinkComponent
      className={baseClasses}
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noreferrer noopener" : undefined}
      {...props}
    >
      {iconVisible && iconPosition === "left" && (
        <Icon color={"primary"} width={24} height={24} />
      )}
      {children}
      {isExternal && (
        <span className="visually-hidden">
          {" "}
          (si apre in una nuova finestra)
        </span>
      )}
      {iconVisible && iconPosition === "right" && (
        <Icon color={"primary"} width={24} height={24} />
      )}
    </LinkComponent>
  );
};
